/**
 * ProfilePage - Full profile view for any user
 * Accessed via /profile/:userId route
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  MessageCircle,
  UserPlus,
  UserCheck,
  UserMinus,
  MapPin,
  Calendar,
  Award,
  Flag,
  Loader2,
  Settings,
  Clock,
  Trophy,
  Target,
  Users,
  X
} from "lucide-react";
import { Button } from "./ui/button";
import { BUBBLE_COLORS } from "./UserBubble";
import UserBubble from "./UserBubble";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import ProfileBadges from "./ProfileBadges";
import { LocalPlayerTrophy } from './top10/LocalPlayerTrophy';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const ProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [sendingRequest, setSendingRequest] = useState(false);
  
  // Buddies modal state
  const [showBuddiesModal, setShowBuddiesModal] = useState(false);
  const [buddies, setBuddies] = useState([]);
  const [loadingBuddies, setLoadingBuddies] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch current user
        const meResponse = await axios.get(`${API}/auth/me`);
        setCurrentUser(meResponse.data);
        
        // Fetch profile - if userId matches current user, get own profile
        const profileUrl = meResponse.data.user_id === userId 
          ? `${API}/profile/me`
          : `${API}/users/${userId}/public-profile`;
        
        const profileResponse = await axios.get(profileUrl);
        setProfile(profileResponse.data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        if (error.response?.status === 401) {
          navigate('/');
        } else {
          toast.error('Failed to load profile');
          navigate(-1);
        }
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchData();
    }
  }, [userId, navigate]);

  const fetchBuddies = async () => {
    try {
      setLoadingBuddies(true);
      const response = await axios.get(`${API}/users/${userId}/buddies`);
      setBuddies(response.data.buddies || []);
    } catch (error) {
      console.error('Error fetching buddies:', error);
      toast.error('Failed to load buddies');
    } finally {
      setLoadingBuddies(false);
    }
  };

  const handleShowBuddies = () => {
    setShowBuddiesModal(true);
    fetchBuddies();
  };

  const handleSendGolfBuddyRequest = async () => {
    try {
      setSendingRequest(true);
      await axios.post(`${API}/golf-buddies/request`, {
        target_user_id: userId,
        message: `${currentUser?.name || 'A golfer'} wants to be your golf buddy!`
      });
      
      toast.success('Golf buddy request sent!');
      setProfile(prev => ({
        ...prev,
        pending_request: { from_user_id: currentUser?.user_id, status: 'pending' }
      }));
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send request';
      if (msg === 'Already golf buddies') {
        toast.info("You're already golf buddies!");
      } else if (msg === 'Request already sent') {
        toast.info('Request already sent');
      } else {
        toast.error(msg);
      }
    } finally {
      setSendingRequest(false);
    }
  };

  const handleAcceptRequest = async () => {
    if (!profile?.pending_request?.request_id) return;
    
    try {
      setSendingRequest(true);
      await axios.post(`${API}/golf-buddies/request/${profile.pending_request.request_id}/accept`);
      toast.success(`You and ${profile.name} are now golf buddies!`);
      setProfile(prev => ({ ...prev, is_golf_buddy: true, pending_request: null }));
    } catch (error) {
      toast.error('Failed to accept request');
    } finally {
      setSendingRequest(false);
    }
  };

  const handleMessage = async () => {
    try {
      // First check if conversation already exists
      const convosResponse = await axios.get(`${API}/conversations`);
      const existingConvo = convosResponse.data.conversations?.find(
        c => c.participants?.some(p => p.user_id === userId)
      );
      
      if (existingConvo) {
        // Navigate to existing conversation
        navigate(`/chat?conversation=${existingConvo.conversation_id}`);
      } else {
        // Create new conversation
        const response = await axios.post(`${API}/conversations`, {
          participant_ids: [userId]
        });
        navigate(`/chat?conversation=${response.data.conversation_id}`);
      }
    } catch (error) {
      console.error("Failed to start conversation:", error);
      // Fallback to chat page
      navigate('/chat');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const isOwnProfile = currentUser?.user_id === userId;
  const colorConfig = BUBBLE_COLORS[profile?.bubble_color] || BUBBLE_COLORS.green;
  const baseShadow = '0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3)';

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--ps-bg-base)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--ps-primary)]" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[var(--ps-bg-base)] flex flex-col items-center justify-center p-4">
        <Flag className="w-12 h-12 text-[var(--ps-text-muted)] mb-4" />
        <h2 className="text-xl font-bold text-[var(--ps-text-primary)] mb-2">Profile Not Found</h2>
        <p className="text-[var(--ps-text-muted)] mb-4">This profile may be private or doesn't exist.</p>
        <Button onClick={() => navigate(-1)} variant="outline">Go Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--ps-bg-base)]">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-[var(--ps-bg-base)]/95 backdrop-blur-sm border-b border-[var(--ps-border)]">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-[var(--ps-bg-hover)] text-[var(--ps-text-primary)]"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold text-[var(--ps-text-primary)]">Profile</h1>
          {isOwnProfile ? (
            <button
              onClick={() => navigate('/settings')}
              className="p-2 rounded-full hover:bg-[var(--ps-bg-hover)] text-[var(--ps-text-primary)]"
              data-testid="settings-btn"
            >
              <Settings className="w-5 h-5" />
            </button>
          ) : (
            <div className="w-9" /> 
          )}
        </div>
      </div>

      {/* Banner */}
      <div className="h-32 bg-gradient-to-r from-green-600 to-green-700 relative">
        {/* Avatar - overlapping banner */}
        <div className="absolute -bottom-16 left-1/2 -translate-x-1/2">
          <div
            className="w-32 h-32 rounded-full overflow-hidden"
            style={{
              border: `4px solid ${colorConfig.color}`,
              boxShadow: profile.is_online 
                ? `${colorConfig.glow}, ${baseShadow}` 
                : baseShadow,
              transition: 'box-shadow 0.3s ease',
              backgroundColor: 'var(--ps-bg-surface)'
            }}
          >
            {profile.picture ? (
              <img src={profile.picture} alt={profile.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-4xl font-bold" style={{ color: colorConfig.color }}>
                {profile.name?.charAt(0) || 'G'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <div className="pt-20 px-4 pb-8">
        {/* Name & Username */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-[var(--ps-text-primary)]">{profile.name}<LocalPlayerTrophy userId={profile.user_id || userId} testId="profile-local-player-trophy" /></h2>
          {profile.username && (
            <p className="text-[var(--ps-text-muted)]">@{profile.username}</p>
          )}
          {profile.is_online && (
            <span className="inline-flex items-center gap-1 text-xs text-green-500 mt-1">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              Online now
            </span>
          )}
          {/* Profile Badges */}
          <div className="flex justify-center mt-3">
            <ProfileBadges userId={userId} size="md" />
          </div>
        </div>

        {/* Action Buttons */}
        {!isOwnProfile && (
          <div className="flex gap-3 justify-center mb-6">
            {profile.is_golf_buddy ? (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--ps-primary)]/10 text-[var(--ps-primary)]">
                <UserCheck className="w-5 h-5" />
                <span className="font-medium">Golf Buddy</span>
              </div>
            ) : profile.pending_request ? (
              profile.pending_request.from_user_id === currentUser?.user_id ? (
                <Button variant="outline" disabled className="border-amber-500 text-amber-500">
                  <Clock className="w-4 h-4 mr-2" />
                  Request Pending
                </Button>
              ) : (
                <Button
                  onClick={handleAcceptRequest}
                  disabled={sendingRequest}
                  className="bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)] text-white"
                >
                  {sendingRequest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Accept Request
                </Button>
              )
            ) : (
              <Button
                onClick={handleSendGolfBuddyRequest}
                disabled={sendingRequest}
                className="bg-[var(--ps-primary)] hover:bg-[var(--ps-primary-dark)] text-white"
              >
                {sendingRequest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Add Golf Buddy
              </Button>
            )}
            
            <Button
              variant="outline"
              onClick={handleMessage}
              className="border-[var(--ps-border)] text-[var(--ps-text-primary)]"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Message
            </Button>
          </div>
        )}

        {isOwnProfile && (
          <div className="flex justify-center mb-6">
            <Button
              onClick={() => navigate('/settings')}
              variant="outline"
              className="border-[var(--ps-border)] text-[var(--ps-text-primary)]"
            >
              <Settings className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          </div>
        )}

        {/* Bio */}
        {profile.bio && (
          <p className="text-center text-[var(--ps-text-secondary)] mb-6 max-w-md mx-auto">
            {profile.bio}
          </p>
        )}

        {/* Quick Info */}
        <div className="flex justify-center gap-6 text-sm text-[var(--ps-text-muted)] mb-6">
          {profile.location && (
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span>{profile.location}</span>
            </div>
          )}
          {profile.home_course && (
            <div className="flex items-center gap-1">
              <Flag className="w-4 h-4" />
              <span>{profile.home_course}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>Joined {formatDate(profile.created_at)}</span>
          </div>
        </div>

        {/* Golf Stats */}
        {(profile.handicap !== null || profile.rounds_count > 0 || profile.stats) && (
          <div className="bg-[var(--ps-bg-surface)] rounded-2xl p-6 mb-6 border border-[var(--ps-border)]">
            <h3 className="text-lg font-semibold text-[var(--ps-text-primary)] mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[var(--ps-primary)]" />
              Golf Stats
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
              <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-4">
                <p className="text-3xl font-bold text-[var(--ps-primary)]">
                  {typeof profile.handicap === 'number' && Number.isFinite(profile.handicap) ? profile.handicap.toFixed(1) : 'Not established'}
                </p>
                <p className="text-xs text-[var(--ps-text-muted)] mt-1">Handicap</p>
              </div>
              <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-4">
                <p className="text-3xl font-bold text-[var(--ps-text-primary)]">
                  {profile.rounds_count ?? profile.stats?.rounds_played ?? 0}
                </p>
                <p className="text-xs text-[var(--ps-text-muted)] mt-1">Rounds</p>
              </div>
              <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-4">
                <p className="text-3xl font-bold text-green-500">
                  {profile.best_score ?? '--'}
                </p>
                <p className="text-xs text-[var(--ps-text-muted)] mt-1">Best Score</p>
              </div>
              <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-4">
                <p className="text-3xl font-bold text-[var(--ps-text-primary)]">
                  {profile.average_score ?? '--'}
                </p>
                <p className="text-xs text-[var(--ps-text-muted)] mt-1">Average</p>
              </div>
            </div>
          </div>
        )}

        {/* Private Profile Notice */}
        {!isOwnProfile && !profile.stats && profile.profile_privacy === 'private' && !profile.is_golf_buddy && (
          <div className="bg-[var(--ps-bg-surface)] rounded-2xl p-6 text-center border border-[var(--ps-border)]">
            <Flag className="w-8 h-8 text-[var(--ps-text-muted)] mx-auto mb-3" />
            <p className="text-[var(--ps-text-muted)]">
              This golfer's detailed stats are private. Become golf buddies to see more!
            </p>
          </div>
        )}

        {/* Golf Buddies Section - Clickable to view list */}
        {(profile.buddies_count > 0 || profile.following_count > 0) && (
          <div className="bg-[var(--ps-bg-surface)] rounded-2xl p-6 mb-6 border border-[var(--ps-border)]">
            <button
              onClick={handleShowBuddies}
              className="w-full flex items-center justify-between hover:opacity-80 transition-opacity"
              data-testid="view-buddies-btn"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[var(--ps-primary)]/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[var(--ps-primary)]" />
                </div>
                <div className="text-left">
                  <p className="text-lg font-semibold text-[var(--ps-text-primary)]">
                    {profile.buddies_count ?? profile.following_count ?? 0} Golf Buddies
                  </p>
                  <p className="text-xs text-[var(--ps-text-muted)]">Tap to view list</p>
                </div>
              </div>
              <ArrowLeft className="w-5 h-5 text-[var(--ps-text-muted)] rotate-180" />
            </button>
          </div>
        )}
      </div>

      {/* Buddies List Modal */}
      <Dialog open={showBuddiesModal} onOpenChange={setShowBuddiesModal}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-hidden flex flex-col" style={{
          background: 'var(--ps-bg-surface)',
          border: '1px solid var(--ps-border)'
        }}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-[var(--ps-text-primary)] flex items-center gap-2">
              <Users className="w-5 h-5 text-[var(--ps-primary)]" />
              {profile?.name}'s Golf Buddies
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {loadingBuddies ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[var(--ps-primary)]" />
              </div>
            ) : buddies.length === 0 ? (
              <div className="text-center py-8 text-[var(--ps-text-muted)]">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No golf buddies yet</p>
              </div>
            ) : (
              <div className="space-y-2 p-1">
                {buddies.map((buddy) => (
                  <button
                    key={buddy.user_id}
                    onClick={() => {
                      setShowBuddiesModal(false);
                      navigate(`/profile/${buddy.user_id}`);
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[var(--ps-bg-hover)] transition-colors text-left"
                    data-testid={`buddy-${buddy.user_id}`}
                  >
                    <UserBubble
                      userId={buddy.user_id}
                      picture={buddy.picture}
                      name={buddy.name}
                      bubbleColor={buddy.bubble_color || 'green'}
                      isOnline={buddy.is_online}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[var(--ps-text-primary)] truncate">{buddy.name}</p>
                      {buddy.username && (
                        <p className="text-xs text-[var(--ps-text-muted)]">@{buddy.username}</p>
                      )}
                      {buddy.handicap !== null && buddy.handicap !== undefined && (
                        <p className="text-xs text-[var(--ps-primary)]">{buddy.handicap} HCP</p>
                      )}
                    </div>
                    {buddy.is_following && !buddy.is_self && (
                      <span className="text-xs text-[var(--ps-primary)] bg-[var(--ps-primary)]/20 px-2 py-0.5 rounded-full">
                        Mutual
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
